import {
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type UseModalProps,
} from "@chakra-ui/react";
import { FormRow, TextField } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { useUpdatePasswordMutation } from "@src/__generated__/graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { fieldToInputProps } from "@src/utils/forms/inputHelpers";
import { observer } from "mobx-react-lite";
import { type FunctionComponent, useState } from "react";
import { useToggle } from "react-use";
import { StoreChangePassword } from "./StoreChangePassword";

export const ModalChangePassword: FunctionComponent<UseModalProps> = observer(
  function ModalChangePassword({ isOpen, onClose }) {
    const [store] = useState(() => {
      return StoreChangePassword.create();
    });
    const [showOldPassword, toggleShowOldPassword] = useToggle(false);
    const [showNewPassword, toggleShowNewPassword] = useToggle(false);

    const [update, { loading }] = useUpdatePasswordMutation({
      ...onError(),
      variables: {
        input: store.form.serialize(),
      },
      onCompleted(data) {
        if (data.updatePassword.id) {
          store.form.form.reset();
          onClose();
        }
      },
    });

    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl" trapFocus={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Change password</Trans>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <FormRow title={<Trans>Old password</Trans>}>
              <TextField
                id="field-user-old-password"
                {...fieldToInputProps(store.form.old_password)}
                type={showOldPassword ? "text" : "password"}
                connectedRight={
                  <IconButton
                    pos="relative"
                    top="-1px"
                    aria-label={
                      showOldPassword
                        ? t`Hide old password`
                        : t`Show old password`
                    }
                    icon={
                      <Icon
                        name="eye"
                        color="#C4CDD5"
                        style={{ transform: "scale(1.4)" }}
                      />
                    }
                    onClick={toggleShowOldPassword}
                    size="sm"
                    variant="ghost"
                  />
                }
              />
            </FormRow>

            <FormRow title={<Trans>New password</Trans>}>
              <TextField
                id="field-user-new-password"
                {...fieldToInputProps(store.form.new_password)}
                type={showNewPassword ? "text" : "password"}
                connectedRight={
                  <IconButton
                    pos="relative"
                    top="-1px"
                    aria-label={
                      showOldPassword
                        ? t`Hide new password`
                        : t`Show new password`
                    }
                    icon={
                      <Icon
                        name="eye"
                        color="#C4CDD5"
                        style={{ transform: "scale(1.4)" }}
                      />
                    }
                    onClick={toggleShowNewPassword}
                    size="sm"
                    variant="ghost"
                  />
                }
              />
            </FormRow>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="grey" onClick={onClose} variant="outline">
              <Trans>Cancel</Trans>
            </Button>
            <Button
              ml={3}
              isDisabled={store.form.form.hasError}
              isLoading={loading}
              onClick={() => update()}
            >
              <Trans>Save</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);
